import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FolderPlus, Folder, FolderOpen, FileText, Plus, Edit2, Trash2,
  ChevronRight, ChevronDown, Search, Loader2, MoreVertical, RefreshCw,
  Upload, Download, X, File, AlertCircle, FolderTree, Check, Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { plantillasAPI } from '../api';
import { toast } from '../hooks/use-toast';
import RichTextEditor from '../components/RichTextEditor';

const Plantillas = () => {
  // Estados principales
  const [loading, setLoading] = useState(true);
  const [carpetas, setCarpetas] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [expandedCarpetas, setExpandedCarpetas] = useState({});
  const [selectedCarpeta, setSelectedCarpeta] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  
  // Estados de diálogos
  const [isCarpetaDialogOpen, setIsCarpetaDialogOpen] = useState(false);
  const [isPlantillaDialogOpen, setIsPlantillaDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // {type: 'carpeta'|'plantilla', item: {...}}
  
  // Estados de formularios
  const [editingCarpeta, setEditingCarpeta] = useState(null);
  const [editingPlantilla, setEditingPlantilla] = useState(null);
  const [carpetaForm, setCarpetaForm] = useState({ nombre: '', descripcion: '', padre_id: null });
  const [plantillaForm, setPlantillaForm] = useState({ nombre: '', descripcion: '', contenido: '', carpeta_id: null, etiquetas: '' });
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewPlantilla, setPreviewPlantilla] = useState(null);
  const fileInputRef = useRef(null);

  // Cargar datos
  const loadCarpetas = useCallback(async () => {
    try {
      const response = await plantillasAPI.getCarpetas();
      setCarpetas(response.data || []);
    } catch (error) {
      console.error('Error cargando carpetas:', error);
      toast({ title: "Error", description: "No se pudieron cargar las carpetas", variant: "destructive" });
    }
  }, []);

  const loadPlantillas = useCallback(async () => {
    try {
      const params = {};
      if (selectedCarpeta !== null) {
        params.carpeta_id = selectedCarpeta;
      }
      if (busqueda) {
        params.busqueda = busqueda;
      }
      const response = await plantillasAPI.getAll(params);
      setPlantillas(response.data || []);
    } catch (error) {
      console.error('Error cargando plantillas:', error);
    }
  }, [selectedCarpeta, busqueda]);

  const loadData = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadCarpetas(), loadPlantillas()]);
    setLoading(false);
  }, [loadCarpetas, loadPlantillas]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadPlantillas();
  }, [loadPlantillas]);

  // Inicializar carpetas por defecto
  const handleInicializarCarpetas = async () => {
    try {
      const response = await plantillasAPI.inicializarCarpetas();
      toast({ title: "Carpetas creadas", description: response.data.message });
      await loadCarpetas();
    } catch (error) {
      toast({ title: "Error", description: "No se pudieron crear las carpetas", variant: "destructive" });
    }
  };

  // Toggle expandir carpeta
  const toggleCarpeta = (carpetaId) => {
    setExpandedCarpetas(prev => ({
      ...prev,
      [carpetaId]: !prev[carpetaId]
    }));
  };

  // Seleccionar carpeta
  const handleSelectCarpeta = (carpetaId) => {
    setSelectedCarpeta(carpetaId === selectedCarpeta ? null : carpetaId);
  };

  // ============== CARPETAS ==============
  
  const handleOpenCarpetaDialog = (carpeta = null, padreId = null) => {
    if (carpeta) {
      setEditingCarpeta(carpeta);
      setCarpetaForm({
        nombre: carpeta.nombre,
        descripcion: carpeta.descripcion || '',
        padre_id: carpeta.padre_id
      });
    } else {
      setEditingCarpeta(null);
      setCarpetaForm({
        nombre: '',
        descripcion: '',
        padre_id: padreId
      });
    }
    setIsCarpetaDialogOpen(true);
  };

  const handleSaveCarpeta = async () => {
    if (!carpetaForm.nombre.trim()) {
      toast({ title: "Error", description: "El nombre es obligatorio", variant: "destructive" });
      return;
    }
    
    try {
      setSaving(true);
      if (editingCarpeta) {
        await plantillasAPI.updateCarpeta(editingCarpeta.id, carpetaForm);
        toast({ title: "Carpeta actualizada" });
      } else {
        await plantillasAPI.createCarpeta(carpetaForm);
        toast({ title: "Carpeta creada" });
      }
      setIsCarpetaDialogOpen(false);
      await loadCarpetas();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Error al guardar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  // ============== PLANTILLAS ==============
  
  const handleOpenPlantillaDialog = async (plantilla = null, carpetaId = null) => {
    setSelectedFile(null);
    if (plantilla) {
      setEditingPlantilla(plantilla);
      // Si la plantilla tiene contenido guardado, cargarlo
      if (plantilla.tiene_contenido || plantilla.contenido) {
        try {
          const response = await plantillasAPI.getById(plantilla.id);
          setPlantillaForm({
            nombre: response.data.nombre,
            descripcion: response.data.descripcion || '',
            contenido: response.data.contenido || '',
            carpeta_id: response.data.carpeta_id,
            etiquetas: response.data.etiquetas || ''
          });
        } catch (error) {
          setPlantillaForm({
            nombre: plantilla.nombre,
            descripcion: plantilla.descripcion || '',
            contenido: '',
            carpeta_id: plantilla.carpeta_id,
            etiquetas: plantilla.etiquetas || ''
          });
        }
      } else {
        setPlantillaForm({
          nombre: plantilla.nombre,
          descripcion: plantilla.descripcion || '',
          contenido: plantilla.contenido || '',
          carpeta_id: plantilla.carpeta_id,
          etiquetas: plantilla.etiquetas || ''
        });
      }
    } else {
      setEditingPlantilla(null);
      setPlantillaForm({
        nombre: '',
        descripcion: '',
        contenido: '',
        carpeta_id: carpetaId || selectedCarpeta,
        etiquetas: ''
      });
    }
    setIsPlantillaDialogOpen(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipos de archivo permitidos
      const allowedTypes = ['.doc', '.docx', '.pdf', '.odt', '.rtf', '.txt'];
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(ext)) {
        toast({ 
          title: "Tipo de archivo no permitido", 
          description: `Solo se permiten: ${allowedTypes.join(', ')}`, 
          variant: "destructive" 
        });
        return;
      }
      // Validar tamaño (máximo 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({ 
          title: "Archivo demasiado grande", 
          description: "El archivo no puede superar los 10MB", 
          variant: "destructive" 
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadFile = async (plantillaId) => {
    if (!selectedFile) return;
    
    try {
      setUploadingFile(true);
      await plantillasAPI.uploadArchivo(plantillaId, selectedFile);
      toast({ title: "Archivo subido correctamente" });
      setSelectedFile(null);
    } catch (error) {
      toast({ title: "Error al subir archivo", description: error.message, variant: "destructive" });
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSavePlantilla = async () => {
    if (!plantillaForm.nombre.trim()) {
      toast({ title: "Error", description: "El nombre es obligatorio", variant: "destructive" });
      return;
    }
    
    try {
      setSaving(true);
      let plantillaId;
      
      if (editingPlantilla) {
        await plantillasAPI.update(editingPlantilla.id, plantillaForm);
        plantillaId = editingPlantilla.id;
        toast({ title: "Plantilla actualizada" });
      } else {
        const response = await plantillasAPI.create(plantillaForm);
        plantillaId = response.data.id;
        toast({ title: "Plantilla creada" });
      }
      
      // Subir archivo si hay uno seleccionado
      if (selectedFile && plantillaId) {
        await handleUploadFile(plantillaId);
      }
      
      setIsPlantillaDialogOpen(false);
      setSelectedFile(null);
      await loadPlantillas();
      await loadCarpetas();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Error al guardar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  // ============== ELIMINAR ==============
  
  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      if (deleteTarget.type === 'carpeta') {
        await plantillasAPI.deleteCarpeta(deleteTarget.item.id);
        toast({ title: "Carpeta eliminada" });
        if (selectedCarpeta === deleteTarget.item.id) {
          setSelectedCarpeta(null);
        }
      } else {
        await plantillasAPI.delete(deleteTarget.item.id);
        toast({ title: "Plantilla eliminada" });
      }
      setIsDeleteDialogOpen(false);
      setDeleteTarget(null);
      await loadData();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar", variant: "destructive" });
    }
  };

  // ============== PREVISUALIZAR ==============
  
  const handlePreviewPlantilla = async (plantilla) => {
    try {
      const response = await plantillasAPI.getById(plantilla.id);
      setPreviewPlantilla(response.data);
      setIsPreviewOpen(true);
    } catch (error) {
      toast({ title: "Error", description: "No se pudo cargar la plantilla", variant: "destructive" });
    }
  };

  // Obtener lista plana de carpetas para el selector
  const getFlatCarpetas = (carpetas, level = 0) => {
    let result = [];
    for (const c of carpetas) {
      result.push({ ...c, level });
      if (c.subcarpetas && c.subcarpetas.length > 0) {
        result = result.concat(getFlatCarpetas(c.subcarpetas, level + 1));
      }
    }
    return result;
  };

  const flatCarpetas = getFlatCarpetas(carpetas);

  // Renderizar árbol de carpetas
  const renderCarpetaTree = (carpeta, level = 0) => {
    const isExpanded = expandedCarpetas[carpeta.id];
    const isSelected = selectedCarpeta === carpeta.id;
    const hasSubcarpetas = carpeta.subcarpetas && carpeta.subcarpetas.length > 0;
    
    return (
      <div key={carpeta.id}>
        <div
          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer group transition-colors ${
            isSelected ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
          }`}
          style={{ paddingLeft: `${8 + level * 16}px` }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); toggleCarpeta(carpeta.id); }}
            className="p-0.5 hover:bg-gray-200 rounded"
          >
            {hasSubcarpetas ? (
              isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
            ) : (
              <span className="w-4 h-4" />
            )}
          </button>
          
          <div
            className="flex items-center gap-2 flex-1 min-w-0"
            onClick={() => handleSelectCarpeta(carpeta.id)}
          >
            {isExpanded ? (
              <FolderOpen className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-blue-600' : 'text-yellow-500'}`} />
            ) : (
              <Folder className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-blue-600' : 'text-yellow-500'}`} />
            )}
            <span className="truncate text-sm font-medium">{carpeta.nombre}</span>
            {carpeta.num_plantillas > 0 && (
              <Badge variant="secondary" className="text-xs ml-auto">{carpeta.num_plantillas}</Badge>
            )}
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleOpenCarpetaDialog(null, carpeta.id)}>
                <FolderPlus className="w-4 h-4 mr-2" />
                Nueva subcarpeta
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleOpenPlantillaDialog(null, carpeta.id)}>
                <Plus className="w-4 h-4 mr-2" />
                Nueva plantilla
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleOpenCarpetaDialog(carpeta)}>
                <Edit2 className="w-4 h-4 mr-2" />
                Editar carpeta
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => { setDeleteTarget({ type: 'carpeta', item: carpeta }); setIsDeleteDialogOpen(true); }}
                className="text-red-600"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar carpeta
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {isExpanded && hasSubcarpetas && (
          <div>
            {carpeta.subcarpetas.map(sub => renderCarpetaTree(sub, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Cargando plantillas...</span>
      </div>
    );
  }

  return (
    <div className="p-6 h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FolderTree className="w-6 h-6 text-blue-600" />
            Plantillas de Documentos
          </h1>
          <p className="text-gray-600">Organiza tus plantillas en carpetas y subcarpetas</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
          <Button variant="outline" onClick={() => handleOpenCarpetaDialog()}>
            <FolderPlus className="w-4 h-4 mr-2" />
            Nueva Carpeta
          </Button>
          <Button onClick={() => handleOpenPlantillaDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Nueva Plantilla
          </Button>
        </div>
      </div>

      <div className="flex gap-6 h-[calc(100vh-220px)]">
        {/* Panel izquierdo - Árbol de carpetas */}
        <Card className="w-80 flex-shrink-0 flex flex-col">
          <CardHeader className="py-3 px-4 border-b">
            <CardTitle className="text-sm font-medium flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Folder className="w-4 h-4" />
                Carpetas
              </span>
              {carpetas.length === 0 && (
                <Button size="sm" variant="outline" onClick={handleInicializarCarpetas}>
                  Crear por defecto
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 overflow-y-auto flex-1">
            {/* Opción "Todas las plantillas" */}
            <div
              className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer mb-1 ${
                selectedCarpeta === null ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
              }`}
              onClick={() => setSelectedCarpeta(null)}
            >
              <span className="w-5" />
              <FileText className="w-4 h-4" />
              <span className="text-sm font-medium">Todas las plantillas</span>
            </div>
            
            {/* Opción "Sin carpeta" */}
            <div
              className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer mb-2 ${
                selectedCarpeta === 0 ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
              }`}
              onClick={() => setSelectedCarpeta(0)}
            >
              <span className="w-5" />
              <File className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">Sin carpeta</span>
            </div>
            
            <div className="border-t pt-2">
              {carpetas.map(carpeta => renderCarpetaTree(carpeta))}
            </div>
          </CardContent>
        </Card>

        {/* Panel derecho - Lista de plantillas */}
        <Card className="flex-1 flex flex-col">
          <CardHeader className="py-3 px-4 border-b">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">
                {selectedCarpeta === null 
                  ? 'Todas las plantillas' 
                  : selectedCarpeta === 0 
                    ? 'Plantillas sin carpeta'
                    : flatCarpetas.find(c => c.id === selectedCarpeta)?.nombre || 'Plantillas'}
              </CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Buscar plantillas..."
                    className="pl-9 w-64"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                  />
                  {busqueda && (
                    <button
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={() => setBusqueda('')}
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4 overflow-y-auto flex-1">
            {plantillas.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No hay plantillas</h3>
                <p className="text-gray-500 mb-4">
                  {busqueda ? 'No se encontraron resultados' : 'Crea tu primera plantilla'}
                </p>
                {!busqueda && (
                  <Button onClick={() => handleOpenPlantillaDialog()}>
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Plantilla
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid gap-3">
                {plantillas.map((plantilla) => (
                  <div
                    key={plantilla.id}
                    className="flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50 group"
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">{plantilla.nombre}</h4>
                      {plantilla.descripcion && (
                        <p className="text-sm text-gray-500 truncate">{plantilla.descripcion}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        {plantilla.tiene_archivo && (
                          <Badge variant="outline" className="text-xs">{plantilla.tipo_archivo?.toUpperCase()}</Badge>
                        )}
                        {plantilla.etiquetas && (
                          <span className="text-xs text-gray-400">{plantilla.etiquetas}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {(plantilla.tiene_contenido || plantilla.tiene_archivo) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePreviewPlantilla(plantilla)}
                          title="Previsualizar"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      {plantilla.tiene_archivo && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(plantillasAPI.getDownloadUrl(plantilla.id), '_blank')}
                          title="Descargar archivo"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleOpenPlantillaDialog(plantilla)} title="Editar">
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => { setDeleteTarget({ type: 'plantilla', item: plantilla }); setIsDeleteDialogOpen(true); }}
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog Carpeta */}
      <Dialog open={isCarpetaDialogOpen} onOpenChange={setIsCarpetaDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCarpeta ? 'Editar Carpeta' : 'Nueva Carpeta'}</DialogTitle>
            <DialogDescription>
              {editingCarpeta ? 'Modifica los datos de la carpeta' : 'Crea una nueva carpeta para organizar plantillas'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={carpetaForm.nombre}
                onChange={(e) => setCarpetaForm({ ...carpetaForm, nombre: e.target.value })}
                placeholder="Nombre de la carpeta"
              />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={carpetaForm.descripcion}
                onChange={(e) => setCarpetaForm({ ...carpetaForm, descripcion: e.target.value })}
                placeholder="Descripción opcional"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Carpeta padre</Label>
              <Select
                value={carpetaForm.padre_id?.toString() || "root"}
                onValueChange={(v) => setCarpetaForm({ ...carpetaForm, padre_id: v === "root" ? null : parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar carpeta padre" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="root">
                    <span className="flex items-center gap-2">
                      <Folder className="w-4 h-4" />
                      Raíz (sin carpeta padre)
                    </span>
                  </SelectItem>
                  {flatCarpetas
                    .filter(c => !editingCarpeta || c.id !== editingCarpeta.id)
                    .map((c) => (
                      <SelectItem key={c.id} value={c.id.toString()}>
                        <span className="flex items-center gap-2" style={{ marginLeft: c.level * 16 }}>
                          <Folder className="w-4 h-4 text-yellow-500" />
                          {c.nombre}
                        </span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCarpetaDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveCarpeta} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingCarpeta ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Plantilla */}
      <Dialog open={isPlantillaDialogOpen} onOpenChange={(open) => { setIsPlantillaDialogOpen(open); if (!open) setSelectedFile(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlantilla ? 'Editar Plantilla' : 'Nueva Plantilla'}</DialogTitle>
            <DialogDescription>
              {editingPlantilla ? 'Modifica los datos de la plantilla' : 'Crea una nueva plantilla de documento'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={plantillaForm.nombre}
                  onChange={(e) => setPlantillaForm({ ...plantillaForm, nombre: e.target.value })}
                  placeholder="Nombre de la plantilla"
                />
              </div>
              <div className="space-y-2">
                <Label>Carpeta</Label>
                <Select
                  value={plantillaForm.carpeta_id?.toString() || "none"}
                  onValueChange={(v) => setPlantillaForm({ ...plantillaForm, carpeta_id: v === "none" ? null : parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sin carpeta" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin carpeta</SelectItem>
                    {flatCarpetas.map((c) => (
                      <SelectItem key={c.id} value={c.id.toString()}>
                        <span style={{ marginLeft: c.level * 16 }}>{c.nombre}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={plantillaForm.descripcion}
                onChange={(e) => setPlantillaForm({ ...plantillaForm, descripcion: e.target.value })}
                placeholder="Descripción de la plantilla"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Etiquetas</Label>
              <Input
                value={plantillaForm.etiquetas}
                onChange={(e) => setPlantillaForm({ ...plantillaForm, etiquetas: e.target.value })}
                placeholder="demanda, civil, primera instancia (separadas por coma)"
              />
            </div>
            
            {/* Subida de archivo */}
            <div className="space-y-2">
              <Label>Archivo adjunto</Label>
              <div className="border-2 border-dashed rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".doc,.docx,.pdf,.odt,.rtf,.txt"
                  className="hidden"
                />
                {selectedFile ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <File className="w-5 h-5 text-blue-600" />
                      <span className="font-medium">{selectedFile.name}</span>
                      <span className="text-sm text-gray-500">
                        ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedFile(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : editingPlantilla?.tiene_archivo ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <File className="w-5 h-5 text-green-600" />
                      <span className="font-medium">{editingPlantilla.nombre_archivo}</span>
                      <Badge variant="outline">{editingPlantilla.tipo_archivo?.toUpperCase()}</Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => window.open(plantillasAPI.getDownloadUrl(editingPlantilla.id), '_blank')}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Descargar
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                        <Upload className="w-4 h-4 mr-1" />
                        Cambiar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="text-center cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">
                      Arrastra un archivo o haz clic para seleccionar
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Formatos: DOC, DOCX, PDF, ODT, RTF, TXT (máx. 10MB)
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Editor de texto enriquecido */}
            <div className="space-y-2">
              <Label>Contenido de la plantilla</Label>
              <p className="text-xs text-gray-500">
                Escribe el contenido de la plantilla o sube un archivo. Puedes usar ambas opciones.
              </p>
              <RichTextEditor
                content={plantillaForm.contenido}
                onChange={(html) => setPlantillaForm({ ...plantillaForm, contenido: html })}
                placeholder="Escribe el contenido de la plantilla aquí..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsPlantillaDialogOpen(false); setSelectedFile(null); }}>
              Cancelar
            </Button>
            <Button onClick={handleSavePlantilla} disabled={saving || uploadingFile}>
              {(saving || uploadingFile) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingPlantilla ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmar Eliminación */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Eliminar {deleteTarget?.type === 'carpeta' ? 'carpeta' : 'plantilla'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === 'carpeta' ? (
                <>
                  Se eliminará la carpeta <strong>{deleteTarget?.item?.nombre}</strong> y todo su contenido 
                  (subcarpetas y plantillas). Esta acción no se puede deshacer.
                </>
              ) : (
                <>
                  Se eliminará la plantilla <strong>{deleteTarget?.item?.nombre}</strong>. 
                  Esta acción no se puede deshacer.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog Previsualizar Plantilla */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              {previewPlantilla?.nombre}
            </DialogTitle>
            {previewPlantilla?.descripcion && (
              <DialogDescription>{previewPlantilla.descripcion}</DialogDescription>
            )}
          </DialogHeader>
          <div className="py-4">
            {previewPlantilla?.tiene_archivo && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <File className="w-5 h-5 text-blue-600" />
                  <span>{previewPlantilla.nombre_archivo}</span>
                  <Badge>{previewPlantilla.tipo_archivo?.toUpperCase()}</Badge>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.open(plantillasAPI.getDownloadUrl(previewPlantilla.id), '_blank')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar
                </Button>
              </div>
            )}
            {previewPlantilla?.contenido ? (
              <div 
                className="prose prose-sm max-w-none p-4 border rounded-lg bg-white"
                dangerouslySetInnerHTML={{ __html: previewPlantilla.contenido }}
              />
            ) : !previewPlantilla?.tiene_archivo ? (
              <p className="text-gray-500 text-center py-8">Esta plantilla no tiene contenido.</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>Cerrar</Button>
            <Button onClick={() => { setIsPreviewOpen(false); handleOpenPlantillaDialog(previewPlantilla); }}>
              <Edit2 className="w-4 h-4 mr-2" />
              Editar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Plantillas;
